/**
 * EstoqueStatus - Componente que mostra status do estoque e ações
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Package, ShoppingCart, CheckCircle, AlertTriangle, 
  Loader2, Info, Warehouse, MapPin 
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const EstoqueStatus = ({
  item,
  projeto,
  embarcacaoIndex,
  onOpenSolicitarCompra,
  onReservaSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [estoqueInfo, setEstoqueInfo] = useState(null);
  const [showReservaDialog, setShowReservaDialog] = useState(false);
  const [quantidadeReserva, setQuantidadeReserva] = useState(1);
  const [localSelecionado, setLocalSelecionado] = useState('');
  const [reservando, setReservando] = useState(false);

  // Buscar produto vinculado ao item (defensivo - itens_custo pode não ser array)
  const itensCusto = Array.isArray(item?.itens_custo) ? item.itens_custo : [];
  const produtoVinculado = itensCusto.find(ic => ic && ic.tipo === 'produto');
  const produtoId = produtoVinculado?.produto_id;

  // IMPORTANTE: useMemo DEVE ficar antes de qualquer early return
  // para não violar as regras de hooks do React (error #310).
  const locaisComEstoque = useMemo(() => 
    ((estoqueInfo && estoqueInfo.estoques) || [])
      .filter(e => (e.quantidade - (e.quantidade_reservada || 0)) > 0)
      .map(e => `${e.local_nome || 'Local'}: ${e.quantidade - (e.quantidade_reservada || 0)}`)
      .join(' | '),
    [estoqueInfo]
  );

  useEffect(() => {
    if (produtoId) {
      fetchEstoqueInfo();
    } else {
      setEstoqueInfo(null);
    }
  }, [produtoId]);

  const fetchEstoqueInfo = async () => {
    if (!produtoId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/produtos/${produtoId}`);
      const produto = response.data;
      
      // Calcular estoque total somando todos os locais
      const estoques = produto.estoques || [];
      const estoqueTotal = estoques.reduce((acc, e) => acc + (e.quantidade || 0), 0);
      const estoqueReservado = estoques.reduce((acc, e) => acc + (e.quantidade_reservada || 0), 0);
      
      
      setEstoqueInfo({
        estoque_atual: estoqueTotal,
        estoque_reservado: estoqueReservado,
        estoque_disponivel: estoqueTotal - estoqueReservado,
        custo_medio: produto.custo_medio || 0,
        unidade: produto.unidade || produto.unidade_medida || 'UN',
        estoques: estoques // Guardar detalhes por local
      });
      
      // Selecionar primeiro local com estoque disponível
      const primeiroLocalComEstoque = estoques.find(e => (e.quantidade - (e.quantidade_reservada || 0)) > 0);
      if (primeiroLocalComEstoque) {
        setLocalSelecionado(primeiroLocalComEstoque.local_id);
      }
    } catch (error) {
      console.error('[EstoqueStatus] Erro ao buscar estoque:', error);
      setEstoqueInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReservar = async () => {
    if (!produtoId || !projeto?.id) {
      toast.error('Dados incompletos para reserva');
      return;
    }

    if (quantidadeReserva <= 0) {
      toast.error('Informe uma quantidade válida');
      return;
    }

    if (!localSelecionado) {
      toast.error('Selecione o local de origem do estoque');
      return;
    }

    // Verificar estoque disponível no local selecionado
    const localEstoque = estoqueInfo?.estoques?.find(e => e.local_id === localSelecionado);
    const disponivelNoLocal = (localEstoque?.quantidade || 0) - (localEstoque?.quantidade_reservada || 0);
    
    if (quantidadeReserva > disponivelNoLocal) {
      toast.error(`Quantidade máxima disponível neste local: ${disponivelNoLocal}`);
      return;
    }

    setReservando(true);
    try {
      await api.post('/reservas-estoque', {
        produto_id: produtoId,
        projeto_id: projeto.id,
        embarcacao_index: embarcacaoIndex ? parseInt(embarcacaoIndex) : null,
        quantidade: parseFloat(quantidadeReserva),
        local_id: localSelecionado
      });

      toast.success('Estoque reservado com sucesso!');
      setShowReservaDialog(false);
      fetchEstoqueInfo(); // Atualizar info
      onReservaSuccess?.();
    } catch (error) {
      console.error('Erro ao reservar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reservar estoque');
    } finally {
      setReservando(false);
    }
  };

  // Se não tem produto vinculado, não mostra nada
  if (!produtoVinculado) {
    return null;
  }

  // Loading
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Verificando estoque...
      </div>
    );
  }

  // Sem info de estoque
  if (!estoqueInfo) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        <Info className="h-4 w-4" />
        Produto vinculado não encontrado no estoque
      </div>
    );
  }

  const temEstoque = estoqueInfo.estoque_disponivel > 0;
  const quantidadeNecessaria = parseFloat(item.quantidade) || 1;
  const estoqueInsuficiente = estoqueInfo.estoque_disponivel < quantidadeNecessaria;
  
  // Estoque disponível no local selecionado
  const localEstoqueSelecionado = estoqueInfo?.estoques?.find(e => e.local_id === localSelecionado);
  const disponivelNoLocalSelecionado = (localEstoqueSelecionado?.quantidade || 0) - (localEstoqueSelecionado?.quantidade_reservada || 0);

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Status do Estoque */}
        <div className="flex items-center gap-3">
          <Warehouse className="h-5 w-5 text-gray-500" />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Estoque:</span>
              {temEstoque ? (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {estoqueInfo.estoque_disponivel} {estoqueInfo.unidade} disponível
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Sem estoque
                </Badge>
              )}
            </div>
            {estoqueInfo.estoque_reservado > 0 && (
              <p className="text-xs text-gray-500 mt-0.5">
                ({estoqueInfo.estoque_reservado} {estoqueInfo.unidade} já reservado)
              </p>
            )}
            {/* Mostrar locais com estoque */}
            {temEstoque && estoqueInfo.estoques?.length > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                <MapPin className="h-3 w-3 inline mr-1" />
                {locaisComEstoque}
              </div>
            )}
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex items-center gap-2">
          {temEstoque && !estoqueInsuficiente ? (
            <Button
              size="sm"
              variant="outline"
              className="text-green-700 border-green-300 hover:bg-green-50"
              onClick={() => {
                setQuantidadeReserva(quantidadeNecessaria);
                setShowReservaDialog(true);
              }}
            >
              <Package className="h-4 w-4 mr-1" />
              Alocar do Estoque
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="text-blue-700 border-blue-300 hover:bg-blue-50"
              onClick={() => onOpenSolicitarCompra(item)}
            >
              <ShoppingCart className="h-4 w-4 mr-1" />
              Solicitar Compra
            </Button>
          )}
          
          {/* Se tem estoque parcial, mostrar ambos */}
          {temEstoque && estoqueInsuficiente && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="text-green-700 border-green-300 hover:bg-green-50"
                onClick={() => {
                  setQuantidadeReserva(estoqueInfo.estoque_disponivel);
                  setShowReservaDialog(true);
                }}
              >
                <Package className="h-4 w-4 mr-1" />
                Alocar {estoqueInfo.estoque_disponivel}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Alerta de estoque insuficiente */}
      {temEstoque && estoqueInsuficiente && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
          <AlertTriangle className="h-4 w-4 inline mr-1" />
          Estoque insuficiente para a quantidade necessária ({quantidadeNecessaria} {estoqueInfo.unidade}). 
          Disponível: {estoqueInfo.estoque_disponivel} {estoqueInfo.unidade}.
        </div>
      )}

      {/* Dialog de Reserva */}
      <Dialog open={showReservaDialog} onOpenChange={setShowReservaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-green-600" />
              Alocar do Estoque
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="font-medium text-green-800">{produtoVinculado.produto_nome}</p>
              <p className="text-sm text-green-700">
                Disponível total: {estoqueInfo.estoque_disponivel} {estoqueInfo.unidade}
              </p>
              <p className="text-sm text-green-600">
                Custo médio: {estoqueInfo.custo_medio.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
            </div>

            {/* Seleção de Local de Origem */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Local de Origem *
              </Label>
              <Select
                value={localSelecionado}
                onValueChange={setLocalSelecionado}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o local de onde sairá o estoque" />
                </SelectTrigger>
                <SelectContent>
                  {estoqueInfo.estoques?.map(estoque => {
                    const disponivel = (estoque.quantidade || 0) - (estoque.quantidade_reservada || 0);
                    return (
                      <SelectItem 
                        key={estoque.local_id} 
                        value={estoque.local_id}
                        disabled={disponivel <= 0}
                      >
                        <span className="flex items-center gap-2">
                          {estoque.local_nome || 'Local sem nome'}
                          <Badge variant={disponivel > 0 ? "default" : "secondary"} className="ml-2">
                            {disponivel} {estoqueInfo.unidade}
                          </Badge>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {localSelecionado && (
                <p className="text-xs text-green-600">
                  ✓ Disponível neste local: {disponivelNoLocalSelecionado} {estoqueInfo.unidade}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Quantidade a reservar *</Label>
              <Input
                type="number"
                min="0.01"
                max={disponivelNoLocalSelecionado || estoqueInfo.estoque_disponivel}
                step="0.01"
                value={quantidadeReserva}
                onChange={(e) => setQuantidadeReserva(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Máximo neste local: {disponivelNoLocalSelecionado || estoqueInfo.estoque_disponivel} {estoqueInfo.unidade}
              </p>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Projeto:</strong> {projeto?.name}
              </p>
              {embarcacaoIndex !== undefined && (
                <p className="text-sm text-blue-700">
                  <strong>Embarcação:</strong> #{parseInt(embarcacaoIndex) + 1}
                </p>
              )}
              <p className="text-sm text-blue-800 mt-1 font-medium">
                Custo total: {(quantidadeReserva * estoqueInfo.custo_medio).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReservaDialog(false)} disabled={reservando}>
              Cancelar
            </Button>
            <Button onClick={handleReservar} disabled={reservando || !localSelecionado}>
              {reservando ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reservando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirmar Reserva
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstoqueStatus;
